// F6: アセット存在チェック（maps JSON の ../assets/* 参照の実在確認）
import { existsSync, readdirSync } from 'node:fs';
import path from 'node:path';
import { REPO_ROOT } from './repo-paths.mjs';
import { requireGameDir, updateGameMeta } from './games.mjs';
import { readJsonIfExists, walkJson } from './json-utils.mjs';
import { auditLog } from './logger.mjs';

const CDN_BASE = 'https://assets.voxelnovels.com/';

export function collectAssetRefs(gameId) {
    const dir = requireGameDir(gameId);
    const mapsDir = path.join(dir, 'maps');
    const refs = new Map(); // assetRel ("models/...") → [{file, jsonPath}]
    const walk = (d) => {
        for (const entry of readdirSync(d, { withFileTypes: true })) {
            const child = path.join(d, entry.name);
            if (entry.isDirectory()) { walk(child); continue; }
            if (!entry.name.endsWith('.json')) continue;
            const json = readJsonIfExists(child);
            if (!json) continue;
            const fileRel = path.relative(dir, child).split(path.sep).join('/');
            walkJson(json, ({ value, path: p }) => {
                // `../assets/` に加えて `../../assets/` のような深い相対参照も同じ物理パス
                //（リポジトリルートの assets/）へ解決する（model-status.mjs と同基準。
                //  固定接頭辞のみだと AI 生成の深い参照が黙って検査対象外＝偽陰性になる）
                const m = typeof value === 'string' ? value.match(/^(?:\.\.\/)+assets\/(.+)$/) : null;
                if (m) {
                    const assetRel = m[1].split('?')[0];
                    if (!refs.has(assetRel)) refs.set(assetRel, []);
                    refs.get(assetRel).push({ file: fileRel, jsonPath: p });
                }
            });
        }
    };
    if (existsSync(mapsDir)) walk(mapsDir);
    return refs;
}

/**
 * ローカル assets/（リポジトリルート・Git 管理外）の実在確認。
 * checkCdn=true で本番 CDN へ HEAD リクエストも行う（ローカルに assets が無い環境向け）。
 */
export async function runAssetCheck(gameId, { checkCdn = false } = {}) {
    const refs = collectAssetRefs(gameId);
    const assetsRoot = path.join(REPO_ROOT, 'assets');
    const localAvailable = existsSync(assetsRoot);
    const missing = [];
    const present = [];
    for (const [assetRel, usages] of refs) {
        const local = localAvailable && existsSync(path.join(assetsRoot, assetRel));
        (local ? present : missing).push({ asset: assetRel, usages });
    }

    let cdnResults = null;
    if (checkCdn && missing.length + present.length > 0) {
        cdnResults = {};
        const targets = checkCdn === 'missing-only' ? missing : [...missing, ...present];
        const queue = [...targets];
        const workers = Array.from({ length: 5 }, async () => {
            while (queue.length > 0) {
                const t = queue.shift();
                if (!t) break;
                try {
                    const res = await fetch(CDN_BASE + t.asset, { method: 'HEAD', signal: AbortSignal.timeout(10000) });
                    cdnResults[t.asset] = res.ok;
                } catch {
                    cdnResults[t.asset] = false;
                }
            }
        });
        await Promise.all(workers);
    }

    const result = {
        ok: missing.length === 0,
        localAssetsDir: localAvailable ? 'assets/' : null,
        totalRefs: refs.size,
        presentCount: present.length,
        missing: missing.map((m) => ({
            asset: m.asset,
            cdn: cdnResults ? cdnResults[m.asset] ?? null : null,
            usages: m.usages.slice(0, 5),
            usageCount: m.usages.length,
        })),
        cdnChecked: !!checkCdn,
    };
    if (!localAvailable) {
        result.warning = 'ローカル assets/ ディレクトリがありません（Git 管理外）。--cdn オプションで本番側の実在確認ができます。';
    }
    updateGameMeta(gameId, (meta) => ({
        ...meta,
        lastValidation: { ...(meta.lastValidation ?? {}), assets: { ok: result.ok, missing: missing.length, at: new Date().toISOString() } },
    }));
    auditLog('validate-assets', { gameId, ok: result.ok, missing: missing.length });
    return result;
}
